import type { HealthRecord, NewHealthRecord, RecordKind } from "../types";

interface HealthTrackerBackup {
  format: "health-tracker-backup";
  version: 1;
  exportedAt: string;
  records: NewHealthRecord[];
}

export const maximumBackupRecords = 5000;

export function serializeBackup(records: HealthRecord[]): string {
  if (records.length > maximumBackupRecords) throw new Error("backup contains too many records");
  const backup: HealthTrackerBackup = {
    format: "health-tracker-backup",
    version: 1,
    exportedAt: new Date().toISOString(),
    records: records.map(toNewRecord),
  };
  return JSON.stringify(backup, null, 2);
}

export function parseBackup(source: string): NewHealthRecord[] {
  const parsed: unknown = JSON.parse(source);
  if (!isObject(parsed) || parsed.format !== "health-tracker-backup" || parsed.version !== 1 || !Array.isArray(parsed.records)) {
    throw new Error("unsupported backup format");
  }
  if (parsed.records.length > maximumBackupRecords) throw new Error("backup contains too many records");
  return parsed.records.map(parseRecord);
}

function toNewRecord(record: HealthRecord): NewHealthRecord {
  const { id: _id, ...input } = record;
  void _id;
  return input;
}

function parseRecord(value: unknown): NewHealthRecord {
  if (!isObject(value) || !isRecordKind(value.kind) || typeof value.recordedAt !== "string" || Number.isNaN(Date.parse(value.recordedAt))) {
    throw new Error("backup contains an invalid record");
  }
  const record: NewHealthRecord = { kind: value.kind, recordedAt: value.recordedAt };
  assignNumber(record, value, "weightKilograms");
  assignNumber(record, value, "systolic");
  assignNumber(record, value, "diastolic");
  assignNumber(record, value, "pulseBpm");
  assignNumber(record, value, "durationMinutes");
  assignString(record, value, "title");
  assignString(record, value, "description");
  // Older backups can contain measurementContext. It is intentionally ignored.
  return record;
}

function assignNumber(target: NewHealthRecord, source: Record<string, unknown>, key: "weightKilograms" | "systolic" | "diastolic" | "pulseBpm" | "durationMinutes") {
  const value = source[key];
  if (value !== undefined) {
    if (typeof value !== "number" || !Number.isFinite(value)) throw new Error("backup contains an invalid numeric value");
    target[key] = value;
  }
}

function assignString(target: NewHealthRecord, source: Record<string, unknown>, key: "title" | "description") {
  const value = source[key];
  if (value !== undefined) {
    if (typeof value !== "string") throw new Error("backup contains an invalid text value");
    target[key] = value;
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isRecordKind(value: unknown): value is RecordKind {
  return value === "weight" || value === "vitals" || value === "exercise" || value === "appointment";
}
